import { FontIconName } from "client/Components";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ReactElement} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { LiveTileContent } from "../LiveTileContent";
import { HubTileCategoryIds } from "./common";

export type ErrorResultProps = {
	itemKind: HubItemKind;
	onRefresh: () => void;
};

export const ErrorResult = (props: ErrorResultProps): ReactElement => {
	const {
		itemKind,
		onRefresh
	} = props;

	const { titleKey, messageKey } = noResultsConfigurationMap[itemKind]
		.error;

	return (
		<LiveTileContent.NoResults
			trackingId="has-error"
			icon="gear"
			title={localize(titleKey)}
			message={localize(messageKey)}
			onRetry={onRefresh}
		/>
	);
};
ErrorResult.displayName = "ErrorResult";

export type EmptyResultProps = {
	itemKind: HubItemKind;
	categoryIds: HubTileCategoryIds;
};

export const EmptyResult = (props: EmptyResultProps): ReactElement => {
	const {
		itemKind,
		categoryIds
	} = props;

	const {
		titleKey,
		icon,
		messageKeys
	} = noResultsConfigurationMap[itemKind]
		.empty;

	const message: LocalizationKey = categoryIds
		? categoryIds.length > 1
			? messageKeys.customCategories
			: messageKeys.category
		: messageKeys.noCategory;

	return (
		<LiveTileContent.NoResults
			trackingId="no-items-yet"
			icon={icon}
			title={localize(titleKey)}
			message={localize(message)}
		/>
	);
};
EmptyResult.displayName = "EmptyResult";

export const SpotlightItemIsNotAvailableResult = (): ReactElement => {
	return (
		<LiveTileContent.NoResults
			trackingId="no-items-yet"
			icon="the-hub"
			title={localize("_ItemNotFound")}
			message={localize("_TheItemMayHaveBeenDeletedOrYouDontHaveThePermissionsToReadIt")}
		/>
	);
};
SpotlightItemIsNotAvailableResult.displayName = "SpotlightItemIsNotAvailableResult";

type EmptyMessageKeys = {
	noCategory: LocalizationKey;
	category: LocalizationKey;
	customCategories: LocalizationKey;
};

type NoResultsConfiguration = {
	error: {
		titleKey: LocalizationKey;
		messageKey: LocalizationKey;
	};
	empty: {
		icon: FontIconName;
		titleKey: LocalizationKey;
		messageKeys: EmptyMessageKeys;
	};
};

const noResultsConfigurationMap: { [key in HubItemKind]: NoResultsConfiguration; } = {
	[HubItemKind.Announcement]: {
		error: {
			titleKey: "_NotAbleToRetrieveAnnouncements",
			messageKey: "_WeWereUnableToRetrieveYourAnnouncements"
		},
		empty: {
			icon: "bullhorn",
			titleKey: "_NoAnnouncementsYet",
			messageKeys: {
				noCategory: "_ThereAreNoPublishedItemsYet",
				category: "_ThereAreNoPublishedItemsInThisCategoryYet",
				customCategories: "_ThereAreNoPublishedItemsYet"
			}
		}
	},
	[HubItemKind.KnowledgeItem]: {
		error: {
			titleKey: "_NotAbleToRetrieveKnowledgeArticles",
			messageKey: "_WeWereUnableToRetrieveYourKnowledgeArticles"
		},
		empty: {
			icon: "book",
			titleKey: "_NoKnowledgeArticlesYet",
			messageKeys: {
				noCategory: "_ThereAreNoPublishedItemsYet",
				category: "_ThereAreNoPublishedItemsInThisCategoryYet",
				customCategories: "_ThereAreNoPublishedItemsYet"
			}
		}
	},
	[HubItemKind.Event]: {
		error: {
			titleKey: "_NotAbleToRetrieveEvents",
			messageKey: "_WeWereUnableToRetrieveYourEvents"
		},
		empty: {
			icon: "calendar-star",
			titleKey: "_NoEventsYet",
			messageKeys: {
				noCategory: "_ThereAreNoPublishedItemsYet",
				category: "_ThereAreNoPublishedItemsInThisCategoryYet",
				customCategories: "_ThereAreNoPublishedItemsYet"
			}
		}
	}
};
